import { useEffect, useState } from "react";

/**
 * AnchorNav — light, refined sub-menu that sits flush below the main Navbar.
 *
 * Visual rules (per brand):
 *   - Light bone surface (sits between navbar and content)
 *   - Champagne hairlines top + bottom for separation
 *   - No vertical gap to the Navbar (top: 63px = Navbar height)
 *   - Items in JetBrains Mono uppercase; active item in champagne
 */
export function AnchorNav({ items }) {
  const [active, setActive] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(`#${entry.target.id}`);
        });
      },
      // Active zone: just below the sticky bar (~108px) down to 60% from top.
      // Keeping it asymmetric prevents flicker when sections are short.
      { rootMargin: "-110px 0px -55% 0px" }
    );
    items.forEach((it) => {
      const el = document.querySelector(it.href);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [items]);

  const scroll = (href) => (e) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (!el) return;
    // Land the section's top exactly at the bottom of the sticky bar
    // (navbar 60px + AnchorNav ~47px = 107). Tiny 1px nudge keeps the
    // bottom champagne hairline visually attached to the section.
    const top = el.getBoundingClientRect().top + window.scrollY - 106;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <nav
      className="sticky top-[60px] z-30 bg-bone border-y border-champagne/40"
      data-testid="anchor-nav"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex items-center gap-7 overflow-x-auto py-2.5 no-scrollbar">
          {items.map((item) => {
            const isActive = active === item.href;
            return (
              <button
                key={item.href}
                onClick={scroll(item.href)}
                data-testid={`anchor-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`relative font-mono text-[11px] uppercase tracking-[0.15em] whitespace-nowrap transition-colors py-1 ${
                  isActive
                    ? "text-champagne"
                    : "text-aubergine/55 hover:text-aubergine"
                }`}
              >
                {item.label}
                {isActive && (
                  <span
                    aria-hidden="true"
                    className="absolute left-0 right-0 -bottom-[11px] h-px bg-champagne"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
